import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel, CircularProgress } from '@mui/material';

import { PLAYER_ICON_DEFAULT } from '../../../../../common/staticData';
import { getComparator, stableSort } from '../../../components/utilities';
import { ActionData, isGoalTag } from '../../../components/common';
import GameService from '../../../../../services/game.service';
import PlayerStatDialog from '../../../leaders/status';
import TeamStatsVideoPlayer from '../stats/videoDialog';
import GameExportToEdits from '../../../games/tabs/overview/exportEdits';

// Column IDs must match SQL function s4u_new_get_players_stats_summary_filtered_complete output
// title: ['line1', 'line2'] for 2-row headers, or 'single' for 1-row
const headCells = [
    { id: 'selected_games_player_played', title: ['Games', ''] },
    { id: 'total_goals', title: ['Goals', ''], action: 'Goal' },
    { id: 'total_assists', title: ['Assists', ''], action: 'Assist' },
    { id: 'total_shots', title: ['Shots', ''], action: 'Shot' },
    { id: 'total_shots_on_target', title: ['Shots', 'Target'], action: 'ShotOnTarget' },
    { id: 'total_passes', title: ['Passes', ''], action: 'Pass' },
    { id: 'total_passes_success', title: ['Pass', 'OK'], action: 'PassesSuccess' },
    { id: 'total_crosses', title: ['Cross', ''], action: 'Cross' },
    { id: 'total_crosses_success', title: ['Cross', 'OK'], action: 'CrossSuccess' },
    { id: 'total_dribbles', title: ['Dribble', ''], action: 'Dribble' },
    { id: 'total_dribbles_success', title: ['Dribble', 'OK'], action: 'DribbleSuccess' },
    { id: 'total_turnovers', title: ['Turnover', ''], action: 'Turnover' },
    { id: 'total_interceptions', title: ['Intercept', ''], action: 'Interception' },
    { id: 'total_tackles', title: ['Tackles', ''], action: 'Tackle' },
    { id: 'total_clearances', title: ['Clear', ''], action: 'Clearance' },
    { id: 'total_blocks', title: ['Blocks', ''], action: 'Blocked' },
    { id: 'total_fouls', title: ['Fouls', ''], action: 'Foul' },
    { id: 'total_draw_fouls', title: ['Drawn', ''], action: 'DrawFoul' },
    { id: 'total_corner', title: ['Corners', ''], action: 'Corner' },
    { id: 'total_free_kicks', title: ['Free', 'Kick'], action: 'FreeKick' }
];

const TeamPlayersStats = ({ teamId, seasonId, leagueId, gameIds, games, t }) => {
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('selected_games_player_played');
    const [statOpen, setStatOpen] = useState(false);
    const [playerStat, setPlayerStat] = useState(null);
    const [playData, setPlayData] = useState([]);
    const [videoOpen, setVideoOpen] = useState(false);
    const [gameList, setGameList] = useState([]);
    const [playerStats, setPlayerStats] = useState([]);
    const [exportOpen, setExportOpen] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [loading, setLoading] = useState(false);

    const { user: currentUser } = useSelector((state) => state.auth);

    const handleRequestSort = (prop) => {
        const isAsc = orderBy === prop && order === 'desc';

        setOrder(isAsc ? 'asc' : 'desc');
        setOrderBy(prop);
    };

    const getSortedPlayers = () => {
        if (playerStats.length > 0) {
            return stableSort(playerStats, getComparator(order, orderBy));
        }
        return [];
    };

    const handleDisplayList = (player) => {
        if (gameIds.length > 0) {
            // Use player data directly - no API call needed (same as Ranking players)
            setPlayerStat(player);
            setStatOpen(true);
        } else {
            window.alert("No selected games. Please click 'Select Games' button to select.");
        }
    };

    const handleDisplayVideo = (cell, player) => {
        if (player && player[cell.id] && player[cell.id] !== 0 && cell.title !== 'Games') {
            GameService.getGamePlayerTags(
                currentUser.id,
                teamId,
                `${player.player_id}`,
                gameIds.join(','),
                ActionData[cell.action].action_id,
                ActionData[cell.action].action_type_id,
                ActionData[cell.action].action_result_id,
                null,
                null,
                null,
                null,
                null
            ).then((res) => {
                // Goal clips are shots only; assists are also tagged result=Goal, so filter them out.
                const tags = cell.action === 'Goal' ? res.filter(isGoalTag) : res;
                setPlayData(
                    tags.map((item) => {
                        return {
                            tag_id: item.id,
                            start_time: item.player_tag_start_time,
                            end_time: item.player_tag_end_time,
                            player_name: item.player_names || item.player_name || item.full_name || '',
                            action_name: item.action_names || item.action_name || '',
                            action_type: item.action_type_names || item.action_type_name || item.action_type || '',
                            action_result: item.action_result_names || item.action_result_name || item.action_result || '',
                            game_id: item.game_id,
                            team_id: teamId,
                            court_area: item.court_area_id,
                            inside_pain: item.inside_the_paint,
                            period: item.period,
                            time_in_game: item.time_in_game,
                            home_team_logo: item.home_team_logo,
                            away_team_logo: item.away_team_logo,
                            home_team_goals: item.home_team_goals ?? item.home_team_goal,
                            away_team_goals: item.away_team_goals ?? item.away_team_goal
                        };
                    })
                );
                setVideoOpen(true);
            });
        }
    };

    const handleExportTags = (cell, player) => (e) => {
        e.preventDefault();

        if (player && player[cell.id] && player[cell.id] !== 0 && cell.title !== 'Games') {
            GameService.getGamePlayerTags(
                currentUser.id,
                teamId,
                `${player.player_id}`,
                gameIds.join(','),
                ActionData[cell.action].action_id,
                ActionData[cell.action].action_type_id,
                ActionData[cell.action].action_result_id,
                null,
                null,
                null,
                null,
                null
            ).then((res) => {
                setPlayData(res);
                setExportOpen(true);
            });
        }
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            if (gameIds.length) {
                setGameList(games.filter((item) => gameIds.includes(item.id)));
                await GameService.getPlayersStatsFiltered({
                    seasonId: seasonId,
                    leagueId: null,
                    gameId: gameIds.join(','),
                    teamId: teamId,
                    playerId: null,
                    gameTime: null,
                    courtAreaId: null,
                    insidePaint: null,
                    homeAway: null,
                    gameResult: null
                }).then((data) => {
                    // Remove duplicates by player_id
                    const uniquePlayers = [];
                    const seenIds = new Set();
                    data.forEach((item) => {
                        if (!seenIds.has(item.player_id)) {
                            seenIds.add(item.player_id);
                            uniquePlayers.push(item);
                        }
                    });
                    setPlayerStats(uniquePlayers);
                });
            } else {
                setPlayerStats([]);
            }
            setLoading(false);
        })();
    }, [refresh, gameIds, teamId, seasonId]);

    return (
        <Box sx={{ width: '100%', background: '#ffffff', flex: 1, display: 'flex', padding: '4px' }}>
            <TableContainer sx={{ flex: 1, maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
                {loading && (
                    <div style={{ width: '100%', height: '100%', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                    </div>
                )}
                {!loading && (
                    <>
                        <Table stickyHeader aria-label="sticky table" size="small" sx={{ tableLayout: 'auto' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        key="name"
                                        align="left"
                                        colSpan={2}
                                        sx={{
                                            backgroundColor: '#17375E',
                                            color: '#ffffff',
                                            fontFamily: "'DM Sans', sans-serif",
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            padding: '6px 8px',
                                            width: '140px',
                                            maxWidth: '140px',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {t('Player Name')}
                                    </TableCell>
                                    {headCells.map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            align="center"
                                            sortDirection={orderBy === cell.id ? order : false}
                                            sx={{
                                                backgroundColor: '#17375E',
                                                color: '#ffffff',
                                                fontFamily: "'DM Sans', sans-serif",
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                                padding: '6px 2px',
                                                lineHeight: 1.2,
                                                maxWidth: '60px'
                                            }}
                                        >
                                            <TableSortLabel
                                                active={orderBy === cell.id}
                                                direction={orderBy === cell.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(cell.id)}
                                                sx={{
                                                    color: '#ffffff !important',
                                                    '& .MuiTableSortLabel-icon': { color: '#ffffff !important', fontSize: '0.7rem' },
                                                    '&.Mui-active': { color: '#ffffff !important' },
                                                    flexDirection: 'column',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <span>{t(cell.title[0])}</span>
                                                <span>{t(cell.title[1])}</span>
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getSortedPlayers().map((player, index) => (
                                    <TableRow
                                        key={player.player_id || index}
                                        hover
                                        sx={{
                                            height: '60px',
                                            backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff',
                                            '&:hover': { backgroundColor: '#e8f4fc' }
                                        }}
                                    >
                                        <TableCell
                                            colSpan={2}
                                            sx={{ cursor: 'pointer', padding: '4px 6px', width: '140px', maxWidth: '140px' }}
                                            onClick={() => handleDisplayList(player)}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <img
                                                    style={{ height: '40px', width: '40px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }}
                                                    src={player.player_image || PLAYER_ICON_DEFAULT}
                                                    alt={player.player_full_name || 'Player'}
                                                />
                                                <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                                    <Typography noWrap sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700, color: '#1a1b1d', lineHeight: 1.3 }}>
                                                        {player.player_full_name || '-'}
                                                    </Typography>
                                                    <Typography noWrap sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.6rem', fontWeight: 500, color: '#707070', lineHeight: 1.2 }}>
                                                        {player.player_jersey_number && player.player_jersey_number < 999 ? `#${player.player_jersey_number}` : ''}
                                                        {player.player_jersey_number && player.player_jersey_number < 999 && player.position_name ? ' ' : ''}
                                                        {player.position_name || ''}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        {headCells.map((cell) => (
                                            <TableCell
                                                key={cell.id}
                                                align="center"
                                                sx={{
                                                    cursor: cell.action ? 'pointer' : 'default',
                                                    padding: '4px 2px',
                                                    fontFamily: "'DM Sans', sans-serif",
                                                    fontSize: '0.7rem',
                                                    fontWeight: 500,
                                                    color: player[cell.id] > 0 ? '#1a1b1d' : '#a5a5a8'
                                                }}
                                                onClick={() => handleDisplayVideo(cell, player)}
                                                onContextMenu={handleExportTags(cell, player)}
                                            >
                                                {cell.id === 'total_saved'
                                                    ? (player[cell.id] || 0) + (player['total_super_save'] || 0)
                                                    : cell.id === 'pass_success_rate'
                                                        ? Number(player[cell.id] || 0).toFixed(1)
                                                        : player[cell.id] ?? 0}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
            </TableContainer>
            <PlayerStatDialog
                t={t}
                open={statOpen}
                onClose={() => setStatOpen(false)}
                player={playerStat}
                games={gameList}
                gameIds={gameIds}
                initialState={playerStat}
                statsData={playerStats}
            />
            <TeamStatsVideoPlayer
                t={t}
                open={videoOpen}
                onClose={(flag) => {
                    setVideoOpen(false);

                    if (flag) setRefresh((r) => !r);
                }}
                video_url={gameList}
                tagList={playData}
            />
            <GameExportToEdits t={t} open={exportOpen} onClose={() => setExportOpen(false)} tagList={playData} isTeams={false} />
        </Box>
    );
};

export default TeamPlayersStats;
